const DEFAULT_TTL_MS = 3 * 60 * 1000;
const DEFAULT_MAX_ENTRIES = 150;

const cacheStore = new Map();
const inFlightStore = new Map();
const metrics = {
  totalRequests: 0,
  cacheHits: 0,
  cacheMisses: 0,
  inFlightHits: 0,
  networkRequests: 0,
  networkErrors: 0,
  totalNetworkTimeMs: 0,
};

const normalizePart = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

export const buildGraphCacheKey = ({ nodeId, hops, scope = 'default' }) => {
  return `${normalizePart(scope)}::${normalizePart(nodeId)}::${normalizePart(hops)}`;
};

const isExpired = (entry, now) => {
  if (!entry) return true;
  return now - entry.createdAt > entry.ttlMs;
};

const pruneOldestEntry = () => {
  if (cacheStore.size === 0) return;

  let oldestKey = null;
  let oldestAccess = Number.POSITIVE_INFINITY;

  cacheStore.forEach((entry, key) => {
    if (entry.lastAccessAt < oldestAccess) {
      oldestAccess = entry.lastAccessAt;
      oldestKey = key;
    }
  });

  if (oldestKey) {
    cacheStore.delete(oldestKey);
  }
};

const enforceMaxEntries = (maxEntries) => {
  while (cacheStore.size > maxEntries) {
    pruneOldestEntry();
  }
};

export const clearGraphDataCache = () => {
  cacheStore.clear();
  inFlightStore.clear();
};

export const resetGraphCacheMetrics = () => {
  metrics.totalRequests = 0;
  metrics.cacheHits = 0;
  metrics.cacheMisses = 0;
  metrics.inFlightHits = 0;
  metrics.networkRequests = 0;
  metrics.networkErrors = 0;
  metrics.totalNetworkTimeMs = 0;
};

export const getCachedGraphData = (key, now = Date.now()) => {
  const entry = cacheStore.get(key);
  if (!entry) return null;

  if (isExpired(entry, now)) {
    cacheStore.delete(key);
    return null;
  }

  entry.lastAccessAt = now;
  entry.hits += 1;

  return entry.value;
};

export const setCachedGraphData = (
  key,
  value,
  { ttlMs = DEFAULT_TTL_MS, maxEntries = DEFAULT_MAX_ENTRIES, now = Date.now() } = {}
) => {
  cacheStore.set(key, {
    value,
    createdAt: now,
    lastAccessAt: now,
    ttlMs,
    hits: 0,
  });

  enforceMaxEntries(maxEntries);
};

export const getGraphCacheStats = (now = Date.now()) => {
  let freshEntries = 0;
  let expiredEntries = 0;

  cacheStore.forEach((entry) => {
    if (isExpired(entry, now)) {
      expiredEntries += 1;
    } else {
      freshEntries += 1;
    }
  });

  return {
    totalEntries: cacheStore.size,
    freshEntries,
    expiredEntries,
    inFlightEntries: inFlightStore.size,
  };
};

export const getGraphCacheMetrics = () => {
  const avgNetworkTimeMs = metrics.networkRequests > 0
    ? metrics.totalNetworkTimeMs / metrics.networkRequests
    : 0;

  const hitRate = metrics.totalRequests > 0
    ? metrics.cacheHits / metrics.totalRequests
    : 0;

  return {
    totalRequests: metrics.totalRequests,
    cacheHits: metrics.cacheHits,
    cacheMisses: metrics.cacheMisses,
    inFlightHits: metrics.inFlightHits,
    networkRequests: metrics.networkRequests,
    networkErrors: metrics.networkErrors,
    totalNetworkTimeMs: metrics.totalNetworkTimeMs,
    avgNetworkTimeMs,
    hitRate,
  };
};

export const fetchGraphDataWithCache = async ({
  key,
  fetcher,
  ttlMs = DEFAULT_TTL_MS,
  maxEntries = DEFAULT_MAX_ENTRIES,
  now = Date.now(),
}) => {
  const requestStart = Date.now();
  const cachedValue = getCachedGraphData(key, now);
  if (cachedValue) {
    metrics.totalRequests += 1;
    metrics.cacheHits += 1;
    return { data: cachedValue, source: 'cache', durationMs: Date.now() - requestStart };
  }

  metrics.cacheMisses += 1;

  if (inFlightStore.has(key)) {
    const pendingData = await inFlightStore.get(key);
    metrics.totalRequests += 1;
    metrics.inFlightHits += 1;
    return { data: pendingData, source: 'in-flight', durationMs: Date.now() - requestStart };
  }

  const networkStart = Date.now();
  const requestPromise = fetcher()
    .then((data) => {
      setCachedGraphData(key, data, { ttlMs, maxEntries });
      return data;
    })
    .finally(() => {
      inFlightStore.delete(key);
    });

  inFlightStore.set(key, requestPromise);

  try {
    const networkData = await requestPromise;
    metrics.totalRequests += 1;
    metrics.networkRequests += 1;
    metrics.totalNetworkTimeMs += Date.now() - networkStart;
    return { data: networkData, source: 'network', durationMs: Date.now() - requestStart };
  } catch (error) {
    metrics.totalRequests += 1;
    metrics.networkErrors += 1;
    metrics.totalNetworkTimeMs += Date.now() - networkStart;
    throw error;
  }
};
